<template>
  <BaseContainer backgroundColor="white">
    <v-container>
      <v-row justify="center">
        <v-col cols="auto">
          <span class="success-icon">{{ passGenError ? "⚠️" : "🎉" }}</span>
        </v-col>
      </v-row>
      <div class="text-container">
        <div class="title">{{ title }}</div>
        <div class="subtitle">
          {{ subtitle }}
        </div>
      </div>

      <!-- In-app webview hint: wallet passes cannot be added from these. -->
      <div v-if="isInAppWebview" class="webview-hint">
        <div class="webview-hint-text">
          Open this page in {{ isAndroid ? "Chrome" : "Safari" }} to add your card.
        </div>
      </div>

      <!-- Recovery only: delivery is automatic, so the sole button on this
           screen is the retry shown when pass generation failed. -->
      <v-row v-if="passGenError" justify="center" class="action-row">
        <v-col cols="auto" class="action-col">
          <v-btn
            class="retry-btn"
            :loading="isRetrying"
            :disabled="isRetrying"
            @click="onRetry"
          >
            Try again
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </BaseContainer>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import BaseContainer from "@/components/Atoms/BaseContainer.vue";
import { track, flushAnalytics } from "@/analytics.js";
import { prepareIOSPass } from "@/firebase-functions.js";
import { signOutCustomer } from "@/stores/authStore.js";

const detectInAppWebview = () => {
  const ua = navigator.userAgent || "";
  return /Instagram|FBAN|FBAV|FB_IAB|TikTok|musical_ly|Line\//i.test(ua);
};

const detectIsAndroid = () => /Android/i.test(navigator.userAgent || "");

export default {
  components: {
    BaseContainer,
  },
  setup() {
    // Read delivery info from router state (history.state) with safe fallbacks.
    const state = (typeof history !== "undefined" && history.state) || {};
    const passType = ref(state.passType || null);
    const passUrl = ref(state.passUrl || null);
    const passGenError = ref(state.passGenError || null);
    const businessId = ref(state.businessId || null);
    // iOS delivery is fired the moment registration completes (see
    // FormComponent), so this flag tells us not to fire it again here.
    const deliveryTriggered = ref(state.deliveryTriggered || false);

    const isInAppWebview = ref(detectInAppWebview());
    const isAndroid = ref(detectIsAndroid());
    const isRetrying = ref(false);

    // When we have no state at all (deep link / back navigation) we must NOT
    // auto-trigger anything - just show the success copy.
    const hasDeliveryContext = computed(
      () => !!passUrl.value || !!passGenError.value
    );

    const title = computed(() =>
      passGenError.value
        ? "We couldn't build your card"
        : "Your rewards card was successfully generated\n"
    );
    const subtitle = computed(() =>
      passGenError.value
        ? "Tap to try again - your account is already set up."
        : "With the rewards card in your wallet you are always ready to collect your next stamp 😎"
    );

    // Fire-and-forget analytics so it never blocks the screen.
    const fireAnalytics = (event) => {
      try {
        track(event, {
          had_pass_gen_error: !!passGenError.value,
          pass_type: passType.value || "unknown",
        });
      } catch (e) {
        // analytics already guards internally; ignore.
      }
    };

    // End the transient customer auth session while staying on this screen.
    // Authenticated state is reserved for business staff (Owner / Administrator
    // / Employee); a customer must never be left persistently signed in.
    // Best-effort and never throws.
    const releaseCustomerSession = () => {
      signOutCustomer();
    };

    // How long the Android success screen is shown before the hard navigation
    // to Google Wallet. Long enough for one paint of the confirmation, no more.
    const ANDROID_CONFIRMATION_DELAY_MS = 250;

    // Perform the actual navigation to the wallet URL.
    const navigateToPass = (url) => {
      // Give the analytics SDK a chance to flush before the page unloads.
      flushAnalytics();
      // iOS: Safari overlays the Add to Apple Wallet sheet and the page stays.
      // Android: navigating to the pay.google.com save URL opens the flow.
      window.location.href = url;
    };

    const onRetry = async () => {
      if (isRetrying.value) return;
      isRetrying.value = true;
      fireAnalytics("wallet_add_retry");
      try {
        let url;
        if (passType.value === "android") {
          // No reliable way to reconstruct the original QR code payload here without
          // the email, so retry is best-effort: if we still hold a URL use it.
          if (passUrl.value) {
            url = passUrl.value;
          } else {
            throw new Error("Unable to retry Google Wallet pass");
          }
        } else {
          // iOS: rebuild the URL with a FRESH id token at click time, and wait
          // for the server to prove it can build the pass. The button's own
          // spinner covers that wait, so a retry that would fail again lands
          // back on this error state instead of navigating into a broken
          // response the customer cannot interpret.
          const prepared = await prepareIOSPass(businessId.value);
          url = prepared.passUrl;
        }
        passUrl.value = url;
        passGenError.value = null;
        navigateToPass(url);
        // Retry succeeded - the session has served its purpose, drop it.
        releaseCustomerSession();
      } catch (e) {
        passGenError.value = e.message || "Retry failed";
      } finally {
        isRetrying.value = false;
      }
    };

    onMounted(() => {
      // Success-screen analytics. The authoritative enrollment_completed event
      // is fired in registrationHandler.js on a successful build - this screen
      // only reports that the success UI was reached.
      fireAnalytics("enrollment_success");

      // No delivery context (deep link / back nav): skip auto-trigger. A
      // returning customer should not be left authenticated, so still release.
      if (!hasDeliveryContext.value) {
        releaseCustomerSession();
        return;
      }

      // Pass generation failed earlier: skip auto-trigger, show retry. Keep the
      // session alive here - the retry needs it to mint a fresh id token; it is
      // released after a successful retry instead.
      if (passGenError.value) {
        return;
      }

      // In-app webview: cannot add passes; skip auto-trigger, show hint.
      if (isInAppWebview.value) {
        releaseCustomerSession();
        return;
      }

      // Auto-attempt delivery (the only delivery path - no manual button).
      // iOS delivery already fired at registration completion, so skip it here
      // when flagged to avoid showing the Add to Wallet sheet twice.
      if (passUrl.value && !deliveryTriggered.value) {
        if (isAndroid.value) {
          // Android: a top-level navigation immediately yanks the user off the
          // success screen. Render the confirmation first, then send them to
          // the Google save URL after a brief delay.
          setTimeout(
            () => navigateToPass(passUrl.value),
            ANDROID_CONFIRMATION_DELAY_MS
          );
        } else {
          // iOS fallback: if delivery was not already triggered upstream,
          // Safari overlays the Add to Apple Wallet sheet and the page stays,
          // so we navigate immediately on mount.
          navigateToPass(passUrl.value);
        }
      }

      // Delivery is under way and the URL is already built - auth is no longer
      // needed, so drop the session while staying on the success screen.
      releaseCustomerSession();
    });

    return {
      title,
      subtitle,
      passGenError,
      isInAppWebview,
      isAndroid,
      isRetrying,
      onRetry,
    };
  },
};
</script>

<style scoped>
.success-icon {
  font-size: 32px;
}

.text-container {
  width: 100%;
  text-align: center;
  margin-bottom: 24px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  display: block;
  margin-bottom: 16px;
  color: rgb(0, 109, 62);
}

.subtitle {
  font-size: 14px;
  font-weight: normal;
  display: block;
  white-space: pre-line;
  color: rgb(0, 109, 62);
}

.webview-hint {
  text-align: center;
  margin-bottom: 16px;
}

.webview-hint-text {
  font-size: 14px;
  font-weight: 600;
  color: rgb(0, 109, 62);
}

.action-row {
  margin-top: 8px;
}

.action-col {
  text-align: center;
}

.retry-btn {
  font-size: 16px !important;
  font-weight: 600 !important;
  border-radius: 999px !important;
  text-transform: none !important;
  height: 48px !important;
  padding: 8px 22px !important;
  background-color: rgb(0, 109, 62) !important;
  color: #ffffff !important;
}
</style>
